/**
 * useToast.js - Composable for toast notification management
 *
 * Applying Composables Pattern from PATTERNS_FROM_BASKET_BASE.md
 * Following ADR-149: Toast Notification System Pattern
 *
 * This composable provides:
 * - Temporary toast notifications (auto-dismiss)
 * - Multiple types: success, error, warning, info
 * - Automatic removal after configurable timeout
 * - Maximum simultaneous toasts limit
 * - Unique ID generation for tracking
 *
 * Legal context: Toast notifications provide immediate feedback to users
 * for actions performed in the legal-tech platform, ensuring clear
 * communication of operation status without storing personal data.
 *
 * GDPR compliance: NO personal data stored, only temporary UI feedback
 * Available to all users regardless of tier
 */

import { ref } from 'vue'

// Global state for toasts (shared across all component instances)
const toasts = ref([])

// Configuration
const DEFAULT_TIMEOUT = 4000 // 4 seconds
const MAX_TOASTS = 3 // Maximum simultaneous toasts

/**
 * Generate unique ID for toast tracking
 */
let toastIdCounter = 0
function generateToastId() {
  return `toast-${Date.now()}-${++toastIdCounter}`
}

/**
 * Get icon name for toast type
 */
function getToastIcon(type) {
  const iconMap = {
    success: 'CheckCircleIcon',
    error: 'XCircleIcon',
    warning: 'ExclamationTriangleIcon',
    info: 'InformationCircleIcon'
  }
  return iconMap[type] || 'InformationCircleIcon'
}

/**
 * Get color classes for toast type
 *
 * Design System V2 semantic colors:
 * - Success: #f0fdf4 (50), #22c55e (500), #15803d (700)
 * - Warning: #fffbeb (50), #f59e0b (500), #b45309 (700)
 * - Error: #fef2f2 (50), #ef4444 (500), #b91c1c (700)
 * - Info: #eff6ff (50), #3b82f6 (500), #1d4ed8 (700)
 */
function getToastColorClasses(type) {
  const colorMap = {
    success: {
      bg: 'bg-green-50/95',
      border: 'border-green-200/50',
      text: 'text-green-700',
      icon: 'text-green-500',
      progress: 'bg-green-500'
    },
    error: {
      bg: 'bg-red-50/95',
      border: 'border-red-200/50',
      text: 'text-red-700',
      icon: 'text-red-500',
      progress: 'bg-red-500'
    },
    warning: {
      bg: 'bg-amber-50/95',
      border: 'border-amber-200/50',
      text: 'text-amber-700',
      icon: 'text-amber-500',
      progress: 'bg-amber-500'
    },
    info: {
      bg: 'bg-blue-50/95',
      border: 'border-blue-200/50',
      text: 'text-blue-700',
      icon: 'text-blue-500',
      progress: 'bg-blue-500'
    }
  }
  return colorMap[type] || colorMap.info
}

export function useToast() {
  /**
   * Show a toast notification
   * @param {string} message - Message to display
   * @param {string} type - Toast type: success, error, warning, info
   * @param {number} timeout - Auto-dismiss timeout in ms (optional)
   */
  const showToast = (message, type = 'info', timeout = DEFAULT_TIMEOUT) => {
    // Validate type
    const validTypes = ['success', 'error', 'warning', 'info']
    const toastType = validTypes.includes(type) ? type : 'info'

    // Create toast object
    const toast = {
      id: generateToastId(),
      message,
      type: toastType,
      icon: getToastIcon(toastType),
      colorClasses: getToastColorClasses(toastType),
      timeout,
      createdAt: Date.now()
    }

    // Enforce maximum toasts limit
    if (toasts.value.length >= MAX_TOASTS) {
      // Remove oldest toast
      toasts.value.shift()
    }

    // Add toast to array
    toasts.value.push(toast)

    // Auto-remove after timeout
    if (timeout > 0) {
      setTimeout(() => {
        removeToast(toast.id)
      }, timeout)
    }

    return toast.id
  }

  /**
   * Remove a specific toast by ID
   * @param {string} toastId - Toast ID to remove
   */
  const removeToast = (toastId) => {
    const index = toasts.value.findIndex(t => t.id === toastId)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  /**
   * Remove all toasts
   */
  const clearAllToasts = () => {
    toasts.value = []
  }

  /**
   * Convenience methods for specific toast types
   */
  const success = (message, timeout) => {
    return showToast(message, 'success', timeout)
  }

  const error = (message, timeout) => {
    return showToast(message, 'error', timeout)
  }

  const warning = (message, timeout) => {
    return showToast(message, 'warning', timeout)
  }

  const info = (message, timeout) => {
    return showToast(message, 'info', timeout)
  }

  return {
    // Core functionality
    showToast,
    removeToast,
    clearAllToasts,

    // Convenience methods
    success,
    error,
    warning,
    info,

    // Reactive toast array
    toasts
  }
}
